import type { AnyAction } from 'redux';
import type { ThunkDispatch } from 'redux-thunk';
import api from 'app/shared/utils/api';
import find from 'lodash/find';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import BuildingV2 from 'app/shared/models/BuildingV2';
import constants from 'app/shared/constants/DispatchConstants';
import HomeHubCollection from 'app/shared/models/HomeHubCollection';

type Area = {
  resourceId?: string;
  name?: string;
};

type NearbyArea = {
  resourceId: string;
  name: string;
};

type Collection = {
  collectionType: string;
};

type ApiResponse = {
  data?: {
    buildings?: Array<unknown>;
    areas?: Array<NearbyArea>;
  };
};

type HomeHubApiResponse = {
  data: Array<Collection>;
};

type BuildingInstance = {
  units: Array<{ maloneLotIdEncoded?: string }>;
};

// Exported for testing
export function getArea({
  recentArea,
  userLocation,
}: {
  recentArea?: Area | null;
  userLocation?: Area | null;
}): Area | null | undefined {
  return isEmpty(recentArea) ? userLocation : recentArea;
}

// Exported for testing
export function convertUserItemsResponse(
  apiResponse: ApiResponse | null | undefined,
): BuildingInstance['units'][number] | false {
  const building = get(apiResponse, 'data.buildings[0]');
  if (isEmpty(building)) {
    return false;
  }
  // @ts-expect-error - BuildingV2 is a constructor function with @ts-nocheck
  const buildingv2: BuildingInstance = new BuildingV2(building);
  const result = find(buildingv2.units, (unit) => {
    return unit && unit.maloneLotIdEncoded;
  }) as BuildingInstance['units'][number] | undefined;

  return result || false;
}

// 5.1 Last viewed listing
export function lastViewed() {
  return function (dispatch: ThunkDispatch<unknown, unknown, AnyAction>): Promise<boolean> {
    // @ts-expect-error - api.user.item.get returns a thunk action but lacks proper type definitions
    return dispatch(api.user.item.get('viewed', 0, 1)).then((response: ApiResponse) => {
      const listing = convertUserItemsResponse(response);
      if (isEmpty(listing)) {
        return false;
      }

      dispatch({
        type: constants.HOMEHUB_CONTINUE_WITH,
        payload: listing,
      });
      return true;
    });
  };
}

// 6.1 Expand search to similar city
export function expandSearch({
  recentArea,
  userLocation,
  limit = 5,
}: {
  recentArea?: Area;
  userLocation?: Area;
  limit?: number;
} = {}) {
  return function (dispatch: ThunkDispatch<unknown, unknown, AnyAction>): Promise<boolean> {
    const area = getArea({ recentArea, userLocation });
    const areaResourceId = get(area, 'resourceId') as string | undefined;
    const areaName = get(area, 'name') as string | undefined;
    if (!areaResourceId) {
      return Promise.resolve(false);
    }

    const params = {
      type: 'city',
      areaResourceId,
      limit,
    };

    // TODO: Could try seo/links/byResourceId
    return dispatch(api.area.nearby(params)).then((nearbyAreasResponse: ApiResponse) => {
      const areas = get(nearbyAreasResponse, 'data.areas') as Array<NearbyArea> | undefined;
      if (isEmpty(areas)) {
        return false;
      }
      dispatch({
        type: constants.HOMEHUB_EXPAND_SEARCH,
        payload: { areas, areaName },
      });
      return true;
    });
  };
}

export function fetchCollectionData() {
  return function (dispatch: ThunkDispatch<unknown, unknown, AnyAction>): Promise<AnyAction> {
    return dispatch(api.homeHub()).then((results: HomeHubApiResponse) => {
      const { data } = results;
      const nonAmenityCollections = data.filter((collection) => {
        return collection.collectionType !== 'popularAmenities';
      });
      const collections = nonAmenityCollections.map((collection) => {
        // while we're processing each collection, get an array of collection typesl
        return new HomeHubCollection(collection);
      });
      return dispatch({
        type: constants.HOMEHUB_COLLECTIONS_LOADED,
        payload: {
          collections,
        },
      });
    });
  };
}
